import React, { createContext, useState, useCallback, useEffect, useRef } from 'react';
import ReactGA from 'react-ga4';
import {
  loadAlbums, saveAlbums, loadActiveAlbum, saveActiveAlbum,
  loadAlbumStickers, saveAlbumStickers, removeAlbumStickers,
} from '../utils/storage';
import { generateUUID } from '../utils/uuid';
import { setUserId } from '../utils/userId';
import { fbLoadAlbums, fbSaveAlbums, fbLoadStickers, fbSaveStickers, fbDeleteAlbum, fbUserExists } from '../utils/firebaseStorage';
import { initAuth, remapAuthToUserId } from '../utils/authService';
import { enqueueSync, initSyncQueue } from '../utils/syncQueue';
import { criarStickersVazios } from '../data/copa2026';
import { playerNames } from '../data/playerNames';

const normalizePlayerName = (name) => {
  if (!name || typeof name !== 'string') return '';
  return name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/^[a-z]{2,5}[\s_-]*\d+[\s_-]*/i, '')
    .replace(/[^a-z0-9]/g, '');
};

const remapByPlayerName = (sigla, oldJogadores) => {
  const targetNames = playerNames[sigla];
  if (!targetNames) return { jogadores: oldJogadores, changed: false };

  const newKeyByNormalizedName = {};
  Object.entries(targetNames).forEach(([newKey, newName]) => {
    newKeyByNormalizedName[normalizePlayerName(newName)] = newKey;
  });

  const result = {};
  Object.keys(targetNames).forEach((newKey) => {
    result[newKey] = { nome: targetNames[newKey], obtida: false };
  });

  let changed = false;
  Object.entries(oldJogadores).forEach(([oldKey, player]) => {
    const normalized = normalizePlayerName(player?.nome);
    const matchedKey = newKeyByNormalizedName[normalized];

    if (matchedKey) {
      if (result[matchedKey].obtida !== (player.obtida || false)) {
        changed = true;
      }
      result[matchedKey] = {
        nome: targetNames[matchedKey],
        obtida: player.obtida || false,
      };
      if (matchedKey !== oldKey) changed = true;
    } else {
      changed = true;
    }
  });

  return { jogadores: result, changed };
};

const migrateStickersData = (stickers) => {
  if (!stickers) return null;

  const migrated = {};
  let hasChanges = false;

  Object.entries(stickers).forEach(([sigla, country]) => {
    if (sigla === 'EXT') {
      hasChanges = true;
      return;
    }

    if (playerNames[sigla]) {
      const { jogadores, changed } = remapByPlayerName(sigla, country.jogadores || {});
      if (changed) hasChanges = true;
      migrated[sigla] = { ...country, jogadores };
    } else {
      migrated[sigla] = { ...country };
    }
  });

  const extrasExistente = migrated['EXTRAS'];
  migrated['EXTRAS'] = {
    sigla: 'EXTRAS',
    nome: 'Extras',
    flag: '⭐',
    grupo: 'EXTRAS',
    theme: 'extras',
    totalFigurinhas: 4,
    jogadores: {
      REGU_01: { nome: 'REGU-1 Regular', obtida: extrasExistente?.jogadores?.REGU_01?.obtida ?? false },
      BRO_01: { nome: 'BRO-1 Bronze', obtida: extrasExistente?.jogadores?.BRO_01?.obtida ?? false },
      PRA_01: { nome: 'PRA-1 Prata', obtida: extrasExistente?.jogadores?.PRA_01?.obtida ?? false },
      OURO_01: { nome: 'OURO-1 Ouro', obtida: extrasExistente?.jogadores?.OURO_01?.obtida ?? false },
    },
  };

  if (!extrasExistente || Object.keys(extrasExistente.jogadores || {}).length === 0) {
    hasChanges = true;
  }

  return { data: migrated, hasChanges };
};

const countObtidas = (stickers) => {
  if (!stickers) return 0;
  return Object.values(stickers).reduce(
    (acc, pais) => acc + Object.values(pais.jogadores).filter((j) => j.obtida).length,
    0
  );
};

export const AlbumsContext = createContext();

export const AlbumsProvider = ({ children }) => {
  const [albums, setAlbums] = useState([]);
  const [activeAlbumId, setActiveAlbumId] = useState(null);
  const [stickers, setStickers] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserIdState] = useState(null);

  // Estado do modal de recuperação de progresso
  const [recoveryState, setRecoveryState] = useState(null);
  // { step: 'input' | 'conflict', cloudAlbums, cloudObtidas, localObtidas, pendingUserId }

  const userIdRef = useRef(null);
  const stickersRef = useRef(null);
  const stickersDirtyRef = useRef(false); // true se o usuário já interagiu após o init

  // Mantém stickersRef sincronizado para uso em callbacks sem criar dependências
  useEffect(() => {
    stickersRef.current = stickers;
  }, [stickers]);

  // Persiste álbuns: Firestore é L1, localStorage é cache
  const persistAlbums = useCallback(async (updatedAlbums, uid) => {
    const id = uid || userIdRef.current;
    if (navigator.onLine) {
      const ok = await fbSaveAlbums(id, updatedAlbums);
      if (ok) {
        saveAlbums(updatedAlbums);
      } else {
        enqueueSync({ type: 'saveAlbums', userId: id, data: updatedAlbums });
      }
    } else {
      enqueueSync({ type: 'saveAlbums', userId: id, data: updatedAlbums });
    }
  }, []);

  // Persiste stickers: Firestore é L1, localStorage é cache
  const persistStickers = useCallback(async (albumId, updatedStickers, uid) => {
    const id = uid || userIdRef.current;
    if (navigator.onLine) {
      const ok = await fbSaveStickers(id, albumId, updatedStickers);
      if (ok) {
        saveAlbumStickers(albumId, updatedStickers);
      } else {
        enqueueSync({ type: 'saveStickers', userId: id, albumId, data: updatedStickers });
      }
    } else {
      enqueueSync({ type: 'saveStickers', userId: id, albumId, data: updatedStickers });
    }
  }, []);

  // Carrega stickers: Firestore é L1, localStorage é fallback offline
  const loadAndMigrateStickers = useCallback(async (albumId, uid) => {
    let s = null;

    if (navigator.onLine) {
      s = await fbLoadStickers(uid || userIdRef.current, albumId);
      if (s) {
        saveAlbumStickers(albumId, s); // atualiza cache local
      }
    }

    // Fallback para cache local se offline ou Firestore não retornou dados
    if (!s) {
      s = loadAlbumStickers(albumId);
    }

    s = s || criarStickersVazios();
    const migrationResult = migrateStickersData(s);
    if (migrationResult.hasChanges && !stickersDirtyRef.current) {
      await persistStickers(albumId, migrationResult.data, uid);
    }
    return migrationResult.data;
  }, [persistStickers]);

  // Inicialização
  useEffect(() => {
    const init = async () => {
      const uid = await initAuth();
      userIdRef.current = uid;
      setUserIdState(uid);

      initSyncQueue();

      const loadedActiveAlbum = loadActiveAlbum();
      let albums = [];

      if (navigator.onLine) {
        // L1: carrega álbuns do Firestore
        const cloudAlbums = await fbLoadAlbums(uid);
        if (cloudAlbums && cloudAlbums.length > 0) {
          albums = cloudAlbums;
          saveAlbums(cloudAlbums); // atualiza cache local
        } else {
          // Firestore vazio: verifica se há dados locais para migrar (usuários antigos)
          const localAlbums = loadAlbums();
          if (localAlbums.length > 0) {
            albums = localAlbums;
            await fbSaveAlbums(uid, localAlbums);
            for (const album of localAlbums) {
              const s = loadAlbumStickers(album.id);
              if (s) await fbSaveStickers(uid, album.id, s);
            }
          }
        }
      } else {
        // Offline: usa cache local
        albums = loadAlbums();
      }

      setAlbums(albums);

      if (loadedActiveAlbum) {
        const s = await loadAndMigrateStickers(loadedActiveAlbum, uid);
        if (!stickersDirtyRef.current) {
          setStickers(s);
        }
      }
      setActiveAlbumId(loadedActiveAlbum);
      setIsLoading(false);
    };

    init();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Cria novo álbum
  const createAlbum = useCallback((name) => {
    if (!name || typeof name !== 'string') throw new Error('Nome do álbum é obrigatório');
    const trimmedName = name.trim();
    if (trimmedName.length === 0) throw new Error('Nome do álbum não pode estar vazio');
    if (trimmedName.length > 50) throw new Error('Nome do álbum não pode ter mais de 50 caracteres');

    const newAlbum = {
      id: generateUUID(),
      name: trimmedName,
      createdAt: new Date().toISOString(),
      color: getRandomColor(),
    };

    const updatedAlbums = [...albums, newAlbum];
    setAlbums(updatedAlbums);
    persistAlbums(updatedAlbums);

    ReactGA.event({ category: 'Album', action: 'Created', label: trimmedName, value: updatedAlbums.length });
    return newAlbum;
  }, [albums, persistAlbums]);

  // Deleta álbum
  const deleteAlbum = useCallback((albumId) => {
    const albumToDelete = albums.find((a) => a.id === albumId);
    const updatedAlbums = albums.filter((album) => album.id !== albumId);
    setAlbums(updatedAlbums);
    persistAlbums(updatedAlbums);
    if (navigator.onLine) {
      fbDeleteAlbum(userIdRef.current, albumId).then(() => removeAlbumStickers(albumId));
    } else {
      removeAlbumStickers(albumId);
    }

    if (albumToDelete) {
      ReactGA.event({ category: 'Album', action: 'Deleted', label: albumToDelete.name });
    }

    if (activeAlbumId === albumId) {
      setActiveAlbumId(null);
      setStickers(null);
      saveActiveAlbum(null);
    }
  }, [albums, activeAlbumId, persistAlbums]);

  // Define álbum ativo
  const setAlbumActive = useCallback(async (albumId) => {
    stickersDirtyRef.current = false;
    setActiveAlbumId(albumId);
    saveActiveAlbum(albumId);
    if (albumId) {
      const s = await loadAndMigrateStickers(albumId);
      if (!stickersDirtyRef.current) setStickers(s);

      const album = albums.find((a) => a.id === albumId);
      if (album) ReactGA.event({ category: 'Album', action: 'Opened', label: album.name });
    } else {
      setStickers(null);
    }
  }, [albums, loadAndMigrateStickers]);

  // Toggle figurinha: otimista — atualiza React imediatamente, reverte se Firestore falhar
  const toggleFigurinha = useCallback((albumId, sigla, key) => {
    const prev = stickersRef.current;
    if (!prev || !prev[sigla] || !prev[sigla].jogadores[key]) return;

    const wasObtained = prev[sigla].jogadores[key].obtida;
    const playerName = prev[sigla].jogadores[key].nome;
    const countryName = prev[sigla].nome;

    const updated = {
      ...prev,
      [sigla]: {
        ...prev[sigla],
        jogadores: {
          ...prev[sigla].jogadores,
          [key]: { ...prev[sigla].jogadores[key], obtida: !wasObtained },
        },
      },
    };

    stickersDirtyRef.current = true;
    setStickers(updated);

    ReactGA.event({
      category: 'Sticker',
      action: wasObtained ? 'Unmarked' : 'Marked',
      label: `${playerName} - ${countryName}`,
    });

    const uid = userIdRef.current;
    if (navigator.onLine) {
      fbSaveStickers(uid, albumId, updated).then((ok) => {
        if (ok) {
          saveAlbumStickers(albumId, updated);
        } else {
          // Firestore falhou: reverte e enfileira para quando reconectar
          setStickers(prev);
          enqueueSync({ type: 'saveStickers', userId: uid, albumId, data: updated });
        }
      });
    } else {
      saveAlbumStickers(albumId, updated);
      enqueueSync({ type: 'saveStickers', userId: uid, albumId, data: updated });
    }
  }, []);

  const getAlbumProgress = useCallback((albumId) => {
    const s = albumId === activeAlbumId ? stickers : loadAlbumStickers(albumId);
    if (!s) return { obtidas: 0, total: 998 };
    const obtidas = Object.values(s).reduce((acc, pais) =>
      acc + Object.values(pais.jogadores).filter((j) => j.obtida).length, 0);
    const total = Object.values(s).reduce((acc, pais) => acc + pais.totalFigurinhas, 0);
    return { obtidas, total };
  }, [activeAlbumId, stickers]);

  // Inicia fluxo de recuperação de progresso (abre modal de input)
  const startRecovery = useCallback(() => {
    setRecoveryState({ step: 'input' });
  }, []);

  const cancelRecovery = useCallback(() => {
    setRecoveryState(null);
  }, []);

  // Usuário submeteu um ID para recuperar
  const submitRecoveryId = useCallback(async (inputId) => {
    const normalized = inputId.trim().toLowerCase();
    if (!normalized) return { error: 'Informe o seu ID.' };

    const exists = await fbUserExists(normalized);
    if (!exists) return { error: 'ID não encontrado. Verifique e tente novamente.' };

    const cloudAlbums = await fbLoadAlbums(normalized);
    const cloudObtidas = cloudAlbums
      ? (await Promise.all(cloudAlbums.map(async (a) => {
          const s = await fbLoadStickers(normalized, a.id);
          return countObtidas(s);
        }))).reduce((a, b) => a + b, 0)
      : 0;

    const localObtidas = albums.reduce((acc, a) => {
      const s = loadAlbumStickers(a.id);
      return acc + countObtidas(s);
    }, 0);

    if (albums.length > 0 && localObtidas > 0) {
      // Conflito: mostra comparação
      setRecoveryState({
        step: 'conflict',
        pendingUserId: normalized,
        cloudAlbums: cloudAlbums || [],
        cloudObtidas,
        localObtidas,
      });
      return { ok: true };
    }

    // Sem conflito: aplica direto
    await applyRecovery(normalized, cloudAlbums || []);
    return { ok: true };
  }, [albums]); // eslint-disable-line react-hooks/exhaustive-deps

  // Aplica dados da nuvem (escolha do usuário ou sem conflito)
  const applyRecovery = useCallback(async (newUserId, cloudAlbums) => {
    setUserId(newUserId);
    userIdRef.current = newUserId;
    setUserIdState(newUserId);
    await remapAuthToUserId(newUserId);

    saveAlbums(cloudAlbums);
    setAlbums(cloudAlbums);

    // Carrega stickers de cada álbum da nuvem para o local
    for (const album of cloudAlbums) {
      const s = await fbLoadStickers(newUserId, album.id);
      if (s) saveAlbumStickers(album.id, s);
    }

    const firstAlbumId = cloudAlbums[0]?.id || null;
    if (firstAlbumId) {
      saveActiveAlbum(firstAlbumId);
      setActiveAlbumId(firstAlbumId);
      const s = await loadAndMigrateStickers(firstAlbumId, newUserId);
      setStickers(s);
    } else {
      saveActiveAlbum(null);
      setActiveAlbumId(null);
      setStickers(null);
    }

    setRecoveryState(null);
  }, [loadAndMigrateStickers]);

  // Usuário escolheu manter dados locais (ignora nuvem, mas atualiza o userId)
  const keepLocalData = useCallback(async () => {
    if (!recoveryState?.pendingUserId) return;
    const newUserId = recoveryState.pendingUserId;
    // Faz upload dos dados locais para o novo ID
    setUserId(newUserId);
    userIdRef.current = newUserId;
    setUserIdState(newUserId);
    await remapAuthToUserId(newUserId);
    await persistAlbums(albums, newUserId);
    for (const album of albums) {
      const s = loadAlbumStickers(album.id);
      if (s) await fbSaveStickers(newUserId, album.id, s);
    }
    setRecoveryState(null);
  }, [recoveryState, albums, persistAlbums]);

  const getRandomColor = () => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E2'];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const value = {
    albums,
    activeAlbumId,
    stickers,
    isLoading,
    userId,
    recoveryState,
    createAlbum,
    deleteAlbum,
    setAlbumActive,
    toggleFigurinha,
    getAlbumProgress,
    startRecovery,
    cancelRecovery,
    submitRecoveryId,
    applyRecovery,
    keepLocalData,
  };

  return <AlbumsContext.Provider value={value}>{children}</AlbumsContext.Provider>;
};

export const useAlbums = () => {
  const context = React.useContext(AlbumsContext);
  if (!context) throw new Error('useAlbums deve ser usado dentro de AlbumsProvider');
  return context;
};
